import { Movie } from '@usheru-hff/shared/domain';

// Helpers local to keep method self-contained/minimal
export const toTextArray = (movie: Movie, value: any): string[] | undefined => {
  if (!value) return undefined;
  const arr = Array.isArray(value) ? value : [value];

  const strings = arr
    .map(v => {
      if (typeof v === 'string') return v.trim() || undefined;

      if (v && typeof v === 'object') {
        // Try common label fields without depending on your exact types
        const name = typeof (v as any).name === 'string' ? (v as any).name.trim() : undefined;
        const title = typeof (v as any).title === 'string' ? (v as any).title.trim() : undefined;
        const label = typeof (v as any).label === 'string' ? (v as any).label.trim() : undefined;
        const englishName = typeof (v as any).englishName === 'string' ? (v as any).englishName.trim() : undefined;
        const code = typeof (v as any).code === 'string' ? (v as any).code.trim() : undefined;
        const code3 = typeof (v as any).code3 === 'string' ? (v as any).code3.trim() : undefined;
        return name || title || label || englishName || code || code3 || undefined;
      }

      return undefined;
    })
    .filter((s): s is string => !!s);

  return strings.length ? strings : undefined;
};

export const toTextOrArray = (movie: Movie, value: any): string | string[] | undefined => {
  const list = toTextArray(movie, value);
  if (!list) return undefined;
  return list.length === 1 ? list[0] : list;
};

export const toSameAs = (movie: Movie): string[] | undefined => {
  const urls: Array<string | undefined> = [];
  const imdb = typeof movie.imdb === 'string' ? movie.imdb.trim() : '';
  const tmdb = typeof movie.tmdb === 'string' ? movie.tmdb.trim() : '';

  if (imdb && imdb.toLowerCase() !== 'null') urls.push(`https://www.imdb.com/title/${imdb}/`);
  if (tmdb && tmdb.toLowerCase() !== 'null') urls.push(`https://www.themoviedb.org/movie/${tmdb}`);

  // official site
  if (movie.officialSite) urls.push(movie.officialSite);

  // social profiles
  if (Array.isArray(movie.socialNetworks)) {
    for (const s of movie.socialNetworks as any[]) {
      const u =
        (s && typeof s === 'object' && typeof (s as any).url === 'string' && (s as any).url.trim()) ||
        (s && typeof s === 'object' && typeof (s as any).link === 'string' && (s as any).link.trim()) ||
        (typeof s === 'string' && s.trim()) ||
        undefined;

      if (u) urls.push(u);
    }
  }

  const deduped = Array.from(new Set(urls.filter(Boolean) as string[]));
  return deduped.length ? deduped : undefined;
};

// clean-deep.ts

/**
 * Recursively removes:
 * - null / undefined
 * - empty strings (after trim)
 * - empty arrays
 * - empty objects
 *
 * Preserves:
 * - numbers (including 0)
 * - booleans (including false)
 * - valid Date strings
 */
export function cleanDeep<T>(value: T): T {
  if (value === null || value === undefined) {
    return undefined as unknown as T;
  }

  // Strings: trim & drop if empty
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return (trimmed.length === 0 ? undefined : trimmed) as unknown as T;
  }

  // Arrays: clean items & drop empties
  if (Array.isArray(value)) {
    const cleaned = value.map(v => cleanDeep(v)).filter(v => v !== undefined);

    return (cleaned.length > 0 ? cleaned : undefined) as unknown as T;
  }

  // Objects: clean props & drop empty objects
  if (typeof value === 'object') {
    const result: Record<string, any> = {};

    for (const [key, val] of Object.entries(value)) {
      const cleanedVal = cleanDeep(val);
      if (cleanedVal !== undefined) {
        result[key] = cleanedVal;
      }
    }

    return (Object.keys(result).length > 0 ? result : undefined) as unknown as T;
  }

  // Numbers, booleans, symbols, etc.
  return value;
}
